import { Language } from 'src/api/language'
import { useTranslation } from 'react-i18next'
import { AddressFragment, CervedSubjectFragment, CreditType, CustomerDetailFragment, CustomerType, MailContactType, PhoneContactType, WebContactType } from 'src/api/api.generated'
import { isEmail, isPhoneNumber, isURL } from 'src/utils/contact'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { MailContactFormData, PhoneContactFormData, CustomerFormData, CompanyFormData, WebContactFormData } from '../customer.form.types'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { TFunction } from 'i18next'

const useCompanyForm = ({ onSubmit, customer }: { customer: CustomerDetailFragment | null; onSubmit: (data: CustomerFormData) => Promise<void> }) => {
  const { t } = useTranslation()
  const tenant = useTenant()

  const companyForm = getCompanySchema(t)

  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = useForm<CompanyForm>({ resolver: zodResolver(companyForm) })

  const submitHandler: SubmitHandler<CompanyForm> = useCallback(
    async (data: CompanyForm) => {
      const mailContacts = data.mailContacts?.reduce<MailContactFormData[]>((previousValue, currentValue, currentIndex) => {
        if (!currentValue.type || !currentValue.contact || !isEmail(currentValue.contact)) return previousValue
        const contact: MailContactFormData = {
          isDefault: currentIndex === 0,
          order: currentIndex,
          type: currentValue.type,
          value: currentValue.contact,
        }
        return [...previousValue, contact]
      }, [])

      const phoneContacts = data.phoneContacts?.reduce<PhoneContactFormData[]>((previousValue, currentValue, currentIndex) => {
        if (!currentValue.type || !currentValue.contact || !isPhoneNumber(currentValue.contact)) return previousValue
        const contact: PhoneContactFormData = {
          isDefault: currentIndex === 0,
          order: currentIndex,
          type: currentValue.type,
          value: currentValue.contact,
        }
        return [...previousValue, contact]
      }, [])

      const webContacts = data.webContacts?.reduce<WebContactFormData[]>((previousValue, currentValue, currentIndex) => {
        if (!currentValue.type || !currentValue.contact) return previousValue
        const contact: WebContactFormData = {
          isDefault: currentIndex === 0,
          order: currentIndex,
          type: currentValue.type,
          value: currentValue.contact,
        }
        return [...previousValue, contact]
      }, [])

      let name: string | undefined = undefined

      if (data.companyName.physicalPerson) {
        if (data.companyName.lastName && data.companyName.firstName) {
          name = `${data.companyName.lastName} ${data.companyName.firstName}`
        } else {
          return
        }
      }

      if (!data.companyName.physicalPerson) {
        if (data.companyName.name) {
          name = data.companyName.name
        } else {
          return
        }
      }

      if (!name) {
        return
      }

      const companyFormData: CompanyFormData = {
        type: CustomerType.COMPANY,
        language: data.language,
        privacy: {
          dataThirdPartyConsent: data.privacyDataThirdParty.consent,
          dataThirdPartyConsentDate: data.privacyDataThirdParty.date,
          managementConsent: data.privacyManagement.consent,
          managementConsentDate: data.privacyManagement.date,
          marketingConsent: data.privacyMarketing.consent,
          marketingConsentDate: data.privacyMarketing.date,
          profiledMarketingConsent: data.privacyProfiledMarketing.consent,
          profiledMarketingConsentDate: data.privacyProfiledMarketing.date,
        },
        mailContacts: mailContacts,
        phoneContacts: phoneContacts,
        webContacts: webContacts,
        physicalPerson: data.companyName.physicalPerson,
        lastName: data.companyName.lastName,
        firstName: data.companyName.firstName,
        name: name,
        address: data.address,
        vatNumber: data.vatNumberFiscalCode.vatNumber,
        fiscalCode: data.vatNumberFiscalCode.fiscalCode,
        sdiCode: data.sdiCode,
        tagsId: data.tagsId,
        credit: data.credit,
        splitPayment: data.splitPayment,
        riferimentoAmministrazione: data.riferimentoAmministrazione,
        stayVatId: data.stayVatId ? data.stayVatId : null,
      }
      await onSubmit(companyFormData)
    },
    [onSubmit],
  )

  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  const setCervedCustomer = useCallback(
    (subject: CervedSubjectFragment, address?: AddressFragment) => {
      if (subject) {
        const physicalPerson = !subject.company_info && subject.person_info ? true : false
        const firstName = subject.person_info?.first_name || ''
        const lastName = subject.person_info?.last_name || ''
        const name = subject.company_info?.business_name || subject.person_info?.name || ''
        setValue('companyName', {
          physicalPerson: physicalPerson,
          firstName: firstName,
          lastName: lastName,
          name: name,
        })

        const vatNumber = subject.company_info?.vat_number || undefined
        const fiscalCode = subject.tax_code || undefined
        setValue('vatNumberFiscalCode', {
          vatNumber: vatNumber,
          fiscalCode: fiscalCode,
        })
      }
      if (address) {
        setValue('address', {
          countryId: address.countryId || null,
          address: address.address || null,
          city: address.city || null,
          municipalityId: address.municipalityId || null,
          postalCode: address.postalCode || null,
          province: address.province || null,
        })
      }
    },
    [setValue],
  )

  useEffect(() => {
    if (customer) {
      setValue(
        'companyName',
        {
          physicalPerson: customer.company?.physicalPerson || false,
          firstName: customer.firstName || '',
          lastName: customer.lastName || '',
          name: customer.name || '',
        },
        { shouldDirty: false },
      )
      const mailContacts = customer.mailContacts?.map((mc) => ({ type: mc.type, contact: mc.value })) || []
      setValue('mailContacts', mailContacts.length > 0 ? mailContacts : [], { shouldDirty: false })
      const phoneContacts = customer.phoneContacts?.map((mc) => ({ type: mc.type, contact: mc.value })) || []
      setValue('phoneContacts', phoneContacts.length > 0 ? phoneContacts : [], { shouldDirty: false })
      const webContacts = customer.webContacts?.map((mc) => ({ type: mc.type, contact: mc.value })) || []
      setValue('webContacts', webContacts.length > 0 ? webContacts : [], { shouldDirty: false })
      setValue(
        'privacyDataThirdParty',
        {
          consent: customer.privacy?.dataThirdPartyConsent || false,
          date: customer.privacy?.dataThirdPartyConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue(
        'privacyMarketing',
        {
          consent: customer.privacy?.marketingConsent || false,
          date: customer.privacy?.marketingConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue(
        'privacyManagement',
        {
          consent: customer.privacy?.managementConsent || false,
          date: customer.privacy?.managementConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue(
        'privacyProfiledMarketing',
        {
          consent: customer.privacy?.profiledMarketingConsent || false,
          date: customer.privacy?.profiledMarketingConsentDate || undefined,
        },
        { shouldDirty: false },
      )
      setValue('language', customer.language, { shouldDirty: false })
      setValue(
        'address',
        customer.address
          ? {
              countryId: customer.address?.countryId || null,
              address: customer.address?.address || null,
              city: customer.address?.city || null,
              municipalityId: customer.address?.municipalityId || null,
              postalCode: customer.address?.postalCode || null,
              province: customer.address?.province || null,
            }
          : null,
        { shouldDirty: false },
      )
      setValue(
        'vatNumberFiscalCode',
        {
          vatNumber: customer.vatNumber || undefined,
          fiscalCode: customer.fiscalCode || undefined,
        },
        { shouldDirty: false },
      )
      setValue('sdiCode', customer.company?.sdiCode || undefined, { shouldDirty: false })
      setValue('tagsId', customer.tagsId || undefined, { shouldDirty: false })
      setValue('credit', customer.company?.defaultCredit ?? CreditType.NC, { shouldDirty: false })
      setValue('splitPayment', customer?.company?.splitPayment ?? false, { shouldDirty: false })
      setValue('riferimentoAmministrazione', customer?.company?.felOptions?.riferimentoAmministrazione ?? false, { shouldDirty: false })
      setValue('stayVatId', customer.stayVatId ?? undefined, { shouldDirty: false })
    } else {
      setValue('privacyDataThirdParty', { consent: false }, { shouldDirty: false })
      setValue('privacyManagement', { consent: false }, { shouldDirty: false })
      setValue('privacyMarketing', { consent: false }, { shouldDirty: false })
      setValue('privacyProfiledMarketing', { consent: false }, { shouldDirty: false })
      setValue('companyName', { physicalPerson: false, name: '' }, { shouldDirty: false })
      setValue('vatNumberFiscalCode', {}, { shouldDirty: false })
      setValue('address', null, { shouldDirty: false })
      setValue('mailContacts', [], { shouldDirty: false })
      setValue('phoneContacts', [], { shouldDirty: false })
      setValue('webContacts', [], { shouldDirty: false })
      setValue('language', tenant.language, { shouldDirty: false })
      setValue('credit', CreditType.NC, { shouldDirty: false })
      setValue('splitPayment', false, { shouldDirty: false })
      setValue('riferimentoAmministrazione', false, { shouldDirty: false })
    }
  }, [])

  return { companyForm, onFormSubmit, control, errors, setCervedCustomer, formValue: watch(), isDirty }
}

function getCompanySchema(t?: TFunction) {
  return z.object({
    companyName: z
      .object({
        physicalPerson: z.boolean(),
        lastName: z.string().optional(),
        firstName: z.string().optional(),
        name: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.physicalPerson && (!data.firstName || data.firstName.length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['firstName'],
          })
        }
        if (data.physicalPerson && (!data.lastName || data.lastName.length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['lastName'],
          })
        }
        if (!data.physicalPerson && (!data.name || data.name.length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            fatal: true,
            path: ['name'],
          })
        }
      }),
    vatNumberFiscalCode: z
      .object({
        vatNumber: z
          .string()
          .optional()
          .transform((vatNumber) => (vatNumber ? vatNumber?.trim() : vatNumber)),
        fiscalCode: z
          .string()
          .optional()
          .transform((fiscalCode) => (fiscalCode ? fiscalCode?.trim() : fiscalCode)),
      })
      .superRefine((data, ctx) => {
        const missingVat = !data.vatNumber || data.vatNumber.length < 1
        const missingFiscalCode = !data.fiscalCode || data.fiscalCode.length < 1

        if (missingVat && missingFiscalCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('pms.common.customer.vat_number_fiscal_code_error') : 'vat number or fiscal code required',
            path: ['vatNumber'],
          })
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('pms.common.customer.vat_number_fiscal_code_error') : 'vat number or fiscal code required',
            path: ['fiscalCode'],
          })
        }
      }),
    mailContacts: z
      .object({
        contact: z.string(),
        type: z.nativeEnum(MailContactType).optional(),
      })
      .superRefine((data, ctx) => {
        if ((isEmail(data.contact) && data.type) || (!data.contact && !data.type)) return
        if (!data.type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['type'],
          })
        }
        if (!data.contact) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['contact'],
          })
        } else if (!isEmail(data.contact)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.invalid_email') : 'required',
            path: ['contact'],
          })
        }
      })
      .array(),
    phoneContacts: z
      .object({
        contact: z.string(),
        type: z.nativeEnum(PhoneContactType).optional(),
      })
      .superRefine((data, ctx) => {
        if ((isPhoneNumber(data.contact) && data.type) || (!data.contact && !data.type)) return
        if (!data.type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['type'],
          })
        }
        if (!data.contact) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['contact'],
          })
        } else if (!isPhoneNumber(data.contact)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.invalid_phone_number') : 'required',
            path: ['contact'],
          })
        }
      })
      .array(),
    webContacts: z
      .object({
        contact: z.string(),
        // .refine((data) => isURL(data), { message: t ? t('validation.invalid_url') }) : '',
        type: z.nativeEnum(WebContactType).optional(),
      })
      .superRefine((data, ctx) => {
        if ((data.contact.length > 1 && data.type && isURL(data.contact)) || (!data.contact && !data.type)) return
        if (!data.type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['type'],
          })
        }
        if (!data.contact) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.required_error') : 'required',
            path: ['contact'],
          })
        }
        if (!isURL(data.contact)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t ? t('validation.invalid_url') : 'required',
            path: ['contact'],
          })
        }
      })
      .array(),
    privacyMarketing: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    privacyDataThirdParty: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    privacyManagement: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    privacyProfiledMarketing: z.object({
      consent: z.boolean(),
      date: z.date().optional(),
    }),
    language: z.enum(Language),
    address: z
      .object({
        countryId: z.string().nullable(),
        address: z.string().nullable(),
        city: z.string().nullable(),
        municipalityId: z.string().nullable(),
        postalCode: z.string().nullable(),
        province: z.string().nullable(),
      })
      .nullable(),
    sdiCode: z.string().optional(),
    tagsId: z.string().min(1).array().optional(),
    credit: z.nativeEnum(CreditType).default(CreditType.NC),
    splitPayment: z.boolean().default(false),
    riferimentoAmministrazione: z.boolean().default(false),
    stayVatId: z.string().optional(),
  })
}

export default useCompanyForm

const companySchema = getCompanySchema()
export type CompanyForm = z.infer<typeof companySchema>
